import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import {Subject} from 'rxjs'
import {tap, takeUntil} from 'rxjs/operators'

@Component({
  selector: 'ptr-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.scss'],
  standalone: false,
})
export class OtpInputComponent implements OnInit, OnDestroy {
  @Input() label: string
  @Input() otpLength: number = 6
  @Output() onInput = new EventEmitter<{isValid: boolean; value: string}>()
  otpForm: FormGroup
  keydown: string = ''
  destroy$ = new Subject<void>()

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  initializeForm(): void {
    this.otpForm = this.formBuilder.group({})
    console.log(this.otpForm.value, 'form value')

    for (let index = 0; index < this.otpLength; index++) {
      const controlName = `ctrl_${index}`
      this.otpForm.addControl(
        controlName,
        new FormControl('', Validators.required),
      )
    }

    this.otpForm.setValidators(this.comparisonValidator())
    this.otpForm.updateValueAndValidity()

    this.otpForm.valueChanges
      .pipe(
        tap((formValue) => {
          const isValid = this.otpForm.valid
          const otpValue = Object.values(formValue).join('')
          this.onInput.emit({isValid, value: otpValue})
        }),
        takeUntil(this.destroy$),
      )
      .subscribe()
  }

  comparisonValidator(): ValidatorFn {
    return (cc: FormGroup): ValidationErrors | null => {
      if (cc.touched) {
        const word = Object.values(cc.controls)
          .filter((control) => control.value)
          .map((control) => control.value)
          .join('')
        if (word.length !== this.otpLength && word.length > 0) {
          return {notMatch: true}
        } else if (word.length === 0) {
          return {required: true}
        } else {
          return null
        }
      }
      return null
    }
  }

  getFormControlFromItem(term: string): FormControl {
    return this.otpForm.get(term) as FormControl
  }

  validateCharacter(event: KeyboardEvent) {
    if (!event.repeat) {
      if (!this.checkIfNumber(event.key) && event.code !== 'Backspace') {
        event.preventDefault()
        return
      }
    }
  }

  keyDownEvent(event: KeyboardEvent): void {
    if (!event.repeat) {
      this.keydown = event.code
    }
  }

  checkIfNumber(code: string): boolean {
    const regex = /^[0-9]$/
    return regex.test(code)
  }

  getMessage(): string {
    return this.otpForm?.hasError('notMatch') ? 'Your code is incomplete' : ''
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.otpForm.disable() : this.otpForm.enable()
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault()
    const pasteData = event.clipboardData
      ? event.clipboardData.getData('text')
      : ''
    const regex = /^[0-9]*$/
    if (!pasteData || !regex.test(pasteData)) return
    const dataArray = pasteData.split('')
    this.otpForm.reset()
    this.otpForm.markAllAsTouched()
    let element = event.target as HTMLElement
    dataArray.forEach((letter, idx) => {
      const controlName = `ctrl_${idx}`
      if (this.otpForm.controls[controlName]) {
        this.otpForm.controls[controlName].setValue(letter)
        element = this.focusNextControl(element)
      }
    })
  }

  changeFocus(event: KeyboardEvent): void {
    if (event.code !== this.keydown) return
    const element = event.target as HTMLElement
    let sibling: HTMLElement | null = null

    if (event.code === 'Backspace') {
      sibling = element.previousElementSibling as HTMLElement
    } else if (this.checkIfNumber(event.key)) {
      sibling = element.nextElementSibling as HTMLElement
    }
    sibling?.focus()
    this.keydown = ''
  }

  focusNextControl(element: HTMLElement): HTMLElement | null {
    const nextElement = element.nextElementSibling as HTMLElement | null
    nextElement?.focus()
    return nextElement
  }
}
